import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Images des 10 étapes du bain du nourrisson - Photos réalistes avec même professionnelle
const BAIN_STEPS = [
  {
    id: 1,
    title: "Étape 1 : Se laver les mains",
    description: "Lavage soigneux des mains au savon pendant 30 secondes minimum AVANT le soin",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/bd2c6dd83f5d55d47ce597b65577092cfe5705c3c2de251e4c557aa3914b81d5.png"
  },
  {
    id: 2,
    title: "Étape 2 : Préparer l'eau du bain",
    description: "Remplir la baignoire, vérifier la température (37°C) avec thermomètre, préparer serviettes et vêtements",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/46f6025b569a4e06da8721987e6cd14faf0361e5b07bbae76de50520bb64ede8.png"
  },
  {
    id: 3,
    title: "Étape 3 : Déshabiller l'enfant",
    description: "Déshabiller doucement l'enfant en lui expliquant ce que l'on va faire",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/f825384bc1cc10d78a2980d963a9981a9924cf2ef00295cf55f5fabc2d42ef27.png"
  },
  {
    id: 4,
    title: "Étape 4 : Mise à l'eau",
    description: "Descendre doucement l'enfant dans l'eau, le coucher sur l'avant-bras, main tenant l'épaule",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/edba0ef60a09fb602635a7bb85e8eebbda3341e937605bee505c5b19628ead9a.png"
  },
  {
    id: 5,
    title: "Étape 5 : Lavage du corps",
    description: "Bébé allongé sur l'avant-bras dans l'eau, savonner délicatement avec l'autre main",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/2e580322051db4a7053b58ee28d7844ad1a3f3aac5890efa27a8c85b5a5df0c7.png"
  },
  {
    id: 6,
    title: "Étape 6 : Lavage des cheveux",
    description: "Bébé toujours sur l'avant-bras dans l'eau, mouiller et savonner doucement le cuir chevelu",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/0ddbd44a6fec8659ae0f3b42f623371513d8ca6250c7f77803850fdb165a797d.png"
  },
  {
    id: 7,
    title: "Étape 7 : Rinçage",
    description: "Rincer délicatement en versant l'eau sur le corps, bébé toujours allongé sur le bras",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/4c37b71534e5450e00077a1f30aa5cf79bddcb711dbde5c7e37d357f7589bce6.png"
  },
  {
    id: 8,
    title: "Étape 8 : Sortie du bain",
    description: "Sortir l'enfant en le tenant contre soi, l'envelopper immédiatement dans une serviette chaude",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/04d8b35f401fd42c6f3ce45f265f68af7bb1df4e80ae63f9c01ad12a5bf60b23.png"
  },
  {
    id: 9,
    title: "Étape 9 : Séchage",
    description: "Sécher par tamponnement, insister sur les plis (cou, aisselles, aines)",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/a43c756deef951636ceb4cd5b8b58ea42910d6ca827b6ac903a65954a43c0667.png"
  },
  {
    id: 10,
    title: "Étape 10 : Habillage",
    description: "Habiller l'enfant avec des vêtements propres et adaptés à la température",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/14389f438304be656d5b550dc02da87464f17b2731ecab5e80321598b3d94c87.png"
  }
];

// Logo APDR complet
const APDR_LOGO_FULL = "https://customer-assets.emergentagent.com/job_0bdb7109-8029-4d81-a486-71beb721288a/artifacts/o3wx8hx1_logo%20apdr%20formation.png";

const BainCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Musique douce de fond
  const BACKGROUND_MUSIC = "https://cdn.pixabay.com/audio/2022/10/25/audio_850edcfd29.mp3";

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % BAIN_STEPS.length);
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMuted]);

  const handlePrev = () => {
    setCurrentStep((prev) => (prev - 1 + BAIN_STEPS.length) % BAIN_STEPS.length);
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % BAIN_STEPS.length);
  };

  const step = BAIN_STEPS[currentStep];

  // Couleur thématique - Bleu cyan pour le bain
  const themeColor = '#0891B2';
  const themeColorLight = '#A5F3FC';
  const themeColorDark = '#0E7490';

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-2xl border-2"
      style={{ minHeight: '480px', borderColor: themeColorLight }}
      data-testid="bain-carousel"
    >
      <audio ref={audioRef} loop>
        <source src={BACKGROUND_MUSIC} type="audio/mpeg" />
      </audio>

      {/* Fond avec logo APDR */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${APDR_LOGO_FULL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08
        }}
      />

      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(224,247,250,0.88) 100%)`
        }}
      />

      {/* Header */}
      <div 
        className="relative z-10 text-white p-4"
        style={{ background: `linear-gradient(to right, ${themeColor}, ${themeColorDark})` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white p-1 shadow-lg">
              <img src={APDR_LOGO_FULL} alt="APDR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Formation APDR de la VAE</h3>
              <p className="text-cyan-100 text-sm">Le bain du nourrisson - 10 étapes</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {currentStep + 1} / {BAIN_STEPS.length}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* Image */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <img 
                src={step.image} 
                alt={step.title}
                className="w-72 h-72 object-cover rounded-2xl shadow-xl border-4 border-white"
                key={step.id}
                style={{ animation: 'fadeIn 0.5s ease-in-out' }}
              />
              <div 
                className="absolute -top-3 -left-3 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg border-2 border-white"
                style={{ background: `linear-gradient(to bottom right, ${themeColor}, ${themeColorDark})` }}
              >
                {step.id}
              </div>
            </div>
          </div>

          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3" style={{ color: themeColor }}>{step.title}</h2>
            <p className="text-gray-700 text-lg mb-6 bg-white/60 p-3 rounded-lg">{step.description}</p>

            {/* Indicateurs */}
            <div className="flex gap-2 justify-center md:justify-start flex-wrap">
              {BAIN_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-3 rounded-full transition-all duration-300`}
                  style={{
                    backgroundColor: index === currentStep ? themeColor : themeColorLight,
                    width: index === currentStep ? '2rem' : '0.75rem'
                  }}
                  data-testid={`bain-carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="relative z-10 bg-white/90 backdrop-blur-sm p-4 flex items-center justify-between border-t" style={{ borderColor: themeColorLight }}>
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-4 py-2 rounded-full transition-all font-medium"
          style={{ backgroundColor: `${themeColorLight}40`, color: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorLight}
          onMouseLeave={(e) => e.target.style.backgroundColor = `${themeColorLight}40`}
          data-testid="bain-carousel-prev"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg`}
            style={{
              backgroundColor: isPlaying ? themeColor : 'white',
              color: isPlaying ? 'white' : themeColor,
              border: isPlaying ? 'none' : `2px solid ${themeColor}`
            }}
            data-testid="bain-carousel-play"
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>

          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all`}
            style={{
              backgroundColor: isMuted ? '#E5E7EB' : `${themeColorLight}40`,
              color: isMuted ? '#6B7280' : themeColor
            }}
            data-testid="bain-carousel-mute"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-full transition-all font-medium"
          style={{ backgroundColor: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorDark}
          onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
          data-testid="bain-carousel-next"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className="relative z-10 h-2" style={{ backgroundColor: themeColorLight }}>
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            background: `linear-gradient(to right, ${themeColorLight}, ${themeColor})`,
            width: `${((currentStep + 1) / BAIN_STEPS.length) * 100}%` 
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default BainCarousel;
